/**
 * Footer Organism
 *
 * A sticky footer component for cart/checkout pages.
 * Displays total price and navigation buttons.
 */

'use client';

import { useTranslation } from '@/app/i18n/client';
import { Button } from '@/components/atoms/buttons';
import CartContext from '@/context/CartProvider';
import type { PartnerBenefitData } from '@/context/PartnerBenefitProvider';
import useCart from '@/hooks/useCart';
import useDonation from '@/hooks/useDonation';
import { formatCurrency } from '@/utils/currency';
import {
  calcAmountWithDiscount,
  getFormattedDiscountCouponValue,
  getFormattedPartnerBenefitValue,
} from '@/utils/discount-coupons';
import { IDiscountCoupon } from '@/utils/types';
import { useParams, useRouter } from 'next/navigation';
import { useContext } from 'react';

interface FooterProps {
  primaryButtonText?: string;
  primaryButtonIsLoading?: boolean;
  primaryButtonDisabled?: boolean;
  secondButtonText?: string;
  primaryButtonOnClick?: () => void;
  secondButtonOnClick?: () => void;
  showSecondaryButton?: boolean;
  discountCoupon?: IDiscountCoupon[] | null;
  partnerBenefit?: PartnerBenefitData | null;
}

export const Footer = ({
  primaryButtonText,
  primaryButtonIsLoading = false,
  primaryButtonDisabled = false,
  secondButtonText,
  primaryButtonOnClick,
  secondButtonOnClick,
  showSecondaryButton = true,
  discountCoupon,
  partnerBenefit,
}: FooterProps) => {
  const { lng } = useParams() as { lng: string };
  const { t } = useTranslation(lng, 'footer');
  const { cartSubtotalFee, cartSubtotalNoFee, cartTicketsNumber, cart } = useCart();
  const { donation } = useDonation();
  const { crossSellPlan } = useContext(CartContext);
  const router = useRouter();

  // Cross-sell bundle: o plano entra no total EXIBIDO (cobrança dele é separada no checkout).
  const crossSellPlanCents = crossSellPlan?.promotionalPrice ?? 0;

  const goToHome = () => {
    router.push('/');
  };

  const goToCart = () => {
    router.push('/cart');
  };

  if (!cartTicketsNumber) return null;

  return (
    <footer className='fixed inset-x-0 bottom-0 z-20 border-gray2 bg-background px-6 py-4 shadow-footer max-md:p-2 md:border-t md:shadow-none'>
      <div className='hidden w-full items-center justify-between md:flex'>
        <p className='text-xl font-medium text-foreground md:block'>
          {t('total')}{' '}
          <span className='text-primary-600'>
            {formatCurrency(
              calcAmountWithDiscount({ cartSubtotalNoFee, cartSubtotalFee, cart }, discountCoupon, partnerBenefit) +
                donation +
                crossSellPlanCents,
              lng as string
            )}
          </span>
          &nbsp;
          {discountCoupon && (
            <span className='text-sm font-semibold text-muted-foreground'>
              {discountCoupon &&
                getFormattedDiscountCouponValue(discountCoupon, { cart, cartSubtotalFee, cartSubtotalNoFee })}
            </span>
          )}
          {partnerBenefit && !discountCoupon?.length && (
            <span className='text-sm font-semibold text-muted-foreground'>
              {getFormattedPartnerBenefitValue(partnerBenefit, { cart, cartSubtotalFee, cartSubtotalNoFee })}
            </span>
          )}
        </p>
        <div className='flex items-center gap-6'>
          {showSecondaryButton && (
            <Button onClick={secondButtonOnClick || goToHome} variant='outline' className='w-45 p-4'>
              {secondButtonText || t('back')}
            </Button>
          )}
          <Button
            onClick={primaryButtonOnClick || goToCart}
            className='w-45 p-4'
            isLoading={primaryButtonIsLoading}
            disabled={primaryButtonDisabled}>
            {primaryButtonText || t('next')}
          </Button>
        </div>
      </div>

      <div className='flex w-full flex-col items-center justify-center md:hidden'>
        <p className='mb-6 flex w-full items-center justify-between py-4 text-2xl font-medium text-foreground md:block'>
          <span>{t('total')} </span>
          <span className='text-primary-600'>
            {formatCurrency(
              calcAmountWithDiscount({ cartSubtotalNoFee, cartSubtotalFee, cart }, discountCoupon, partnerBenefit) +
                donation +
                crossSellPlanCents,
              lng as string
            )}
          </span>
          {discountCoupon && (
            <span className='text-sm font-semibold text-muted-foreground'>
              {discountCoupon &&
                getFormattedDiscountCouponValue(discountCoupon, { cart, cartSubtotalFee, cartSubtotalNoFee })}
            </span>
          )}
          {partnerBenefit && !discountCoupon?.length && (
            <span className='text-sm font-semibold text-muted-foreground'>
              {getFormattedPartnerBenefitValue(partnerBenefit, { cart, cartSubtotalFee, cartSubtotalNoFee })}
            </span>
          )}
        </p>

        <div className='flex w-full items-center justify-between gap-2'>
          {showSecondaryButton && (
            <Button onClick={secondButtonOnClick || goToHome} variant='outline' className='h-[48px] w-full border p-3'>
              {secondButtonText || t('back')}
            </Button>
          )}

          <Button
            onClick={primaryButtonOnClick || goToCart}
            className='h-[48px] w-full p-3'
            isLoading={primaryButtonIsLoading}
            disabled={primaryButtonDisabled}>
            {primaryButtonText || t('viewCartWithCount', { count: cartTicketsNumber })}
          </Button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
